/**
 * Normalizza il nome esercizio per confronti duplicati (approvazioni admin).
 */
export function normalizeExerciseName(name) {
  if (!name || typeof name !== 'string') return '';
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function levenshtein(a, b) {
  if (a === b) return 0;
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const dp = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i += 1) dp[i][0] = i;
  for (let j = 0; j <= n; j += 1) dp[0][j] = j;
  for (let i = 1; i <= m; i += 1) {
    for (let j = 1; j <= n; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(dp[i - 1][j] + 1, dp[i][j - 1] + 1, dp[i - 1][j - 1] + cost);
    }
  }
  return dp[m][n];
}

/**
 * @returns {number} 0–1 similarità (1 = identico)
 */
export function exerciseNameSimilarity(nameA, nameB) {
  const a = normalizeExerciseName(nameA);
  const b = normalizeExerciseName(nameB);
  if (!a || !b) return 0;
  if (a === b) return 1;
  if (a.includes(b) || b.includes(a)) {
    const shorter = Math.min(a.length, b.length);
    const longer = Math.max(a.length, b.length);
    return shorter / longer >= 0.6 ? 0.92 : 0.75;
  }
  const dist = levenshtein(a, b);
  const maxLen = Math.max(a.length, b.length);
  return Math.max(0, 1 - dist / maxLen);
}

const DEFAULT_THRESHOLD = 0.82;

/**
 * Raggruppa esercizi con nomi molto simili (union-find semplice).
 * @returns {Array<{ key: string, label: string, exercises: object[], isDuplicateGroup: boolean }>}
 */
export function clusterExercisesBySimilarName(exercises, threshold = DEFAULT_THRESHOLD) {
  if (!exercises?.length) return [];

  const parent = exercises.map((_, i) => i);

  const find = (i) => {
    if (parent[i] !== i) parent[i] = find(parent[i]);
    return parent[i];
  };

  const union = (i, j) => {
    const ri = find(i);
    const rj = find(j);
    if (ri !== rj) parent[rj] = ri;
  };

  for (let i = 0; i < exercises.length; i += 1) {
    for (let j = i + 1; j < exercises.length; j += 1) {
      if (exerciseNameSimilarity(exercises[i].nome, exercises[j].nome) >= threshold) {
        union(i, j);
      }
    }
  }

  const buckets = new Map();
  exercises.forEach((ex, idx) => {
    const root = find(idx);
    if (!buckets.has(root)) buckets.set(root, []);
    buckets.get(root).push(ex);
  });

  const groups = Array.from(buckets.values()).map((items) => {
    const sorted = [...items].sort((x, y) => (x.nome || '').localeCompare(y.nome || '', 'it'));
    const label = sorted[0]?.nome || '—';
    return {
      key: normalizeExerciseName(label) || String(sorted[0]?.id),
      label,
      exercises: sorted,
      isDuplicateGroup: sorted.length > 1
    };
  });

  groups.sort((a, b) => {
    if (a.isDuplicateGroup !== b.isDuplicateGroup) return a.isDuplicateGroup ? -1 : 1;
    return a.label.localeCompare(b.label, 'it');
  });

  return groups;
}

/**
 * Altri esercizi nella lista molto simili a questo (escluso se stesso).
 */
export function findSimilarInList(exercise, allExercises, threshold = DEFAULT_THRESHOLD) {
  return (allExercises || []).filter(
    (other) =>
      other.id !== exercise.id &&
      exerciseNameSimilarity(exercise.nome, other.nome) >= threshold
  );
}
